import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Trash, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-73 — Org delete confirm modal.
 *
 * Two-step confirm: (1) fetches deletion-impact so the operator can see
 * every row that would be swept, keyed by collection; (2) requires the
 * operator to type the org name to enable the red Delete button.
 * Super_admin-only surface (backend enforces).
 */
export default function OrgDeleteDialog({ org, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!org?.id) return;
    setLoading(true);
    api.get(`/organizations/${org.id}/deletion-impact`)
      .then((r) => setImpact(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load impact");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [org?.id, onClose]);

  const canDelete = typedName.trim() === (org?.name || "").trim();

  const doDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      const r = await api.delete(
        `/organizations/${org.id}?cascade=true`,
      );
      const total = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${org.name} (${total} rows swept)`);
      onDeleted?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || JSON.stringify(detail) || "Delete failed";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  if (!org) return null;
  const rows = Object.entries(impact?.impact || {});
  const total = rows.reduce((a, [, v]) => a + v, 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="org-delete-modal"
    >
      <div className="brut-card bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b-4 border-black flex items-start justify-between gap-3 bg-rose-50">
          <div>
            <div className="label-mono">Danger zone</div>
            <h2 className="font-chivo font-black text-lg md:text-xl uppercase tracking-tighter">
              Delete {org.type}: {org.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="org-delete-close"
            className="border border-black px-2 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white"
          >
            <X size={10} weight="bold" /> Cancel
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-start gap-2 text-xs text-rose-900 bg-rose-100 border border-rose-800 p-2">
            <Warning size={14} weight="fill" />
            <span>
              This deletes the org AND every row scoped to it. There is no undo —
              only run this before real prod data is loaded.
            </span>
          </div>

          {loading ? (
            <div className="label-mono text-zinc-500">Computing impact…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-zinc-600">
              No related data — this is a clean delete (org record only).
            </div>
          ) : (
            <div>
              <div className="label-mono mb-2">
                This will sweep {total} row{total === 1 ? "" : "s"}:
              </div>
              <div className="border border-black" data-testid="org-delete-impact">
                {rows.sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-[1fr_auto] text-xs border-b border-zinc-200 last:border-b-0"
                    data-testid={`impact-row-${k}`}
                  >
                    <div className="p-2 font-mono text-[11px]">{k.replace(/_/g, " ")}</div>
                    <div className="p-2 font-mono font-bold text-right">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label-mono">
              Type <span className="font-mono">{org.name}</span> to confirm:
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={org.name}
              data-testid="org-delete-name-input"
              className="w-full border border-black p-2 mt-1 font-mono text-xs"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t-4 border-black p-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={doDelete}
            disabled={!canDelete || deleting || loading}
            data-testid="org-delete-confirm"
            className="bg-rose-700 text-white px-4 py-2 text-xs uppercase tracking-widest border border-black inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Trash size={12} weight="bold" />
            {deleting ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
